(ns shared.core.amelia-light.components.dialog.views
  (:require [shared.core.amelia-light.components.dialog.data]
            [shared.core.amelia-light.components.button :as button]
            [shared.core.reactive :as re]
            ["@up/base-ui/components/ui/dialog" :refer [Dialog
                                                        DialogTrigger
                                                        DialogClose
                                                        DialogContent
                                                        DialogHeader
                                                        DialogBody
                                                        DialogFooter
                                                        DialogTitle
                                                        DialogDescription
                                                        DialogPortal]]))

(defmulti
  dialog-view
  identity)

(defn up-dialog [& args] (into [:> Dialog] args))
(defn up-dialog-trigger [& args] (into [:> DialogTrigger] args))
(defn up-dialog-close [& args] (into [:> DialogClose] args))
(defn up-dialog-content [& args] (into [:> DialogContent] args))
(defn up-dialog-header [& args] (into [:> DialogHeader] args))
(defn up-dialog-body [& args] (into [:> DialogBody] args))
(defn up-dialog-footer [& args] (into [:> DialogFooter] args))
(defn up-dialog-title [& args] (into [:> DialogTitle] args))
(defn up-dialog-description [& args] (into [:> DialogDescription] args))
(defn up-dialog-portal [& args] (into [:> DialogPortal] args))

(defn dialog [{:keys [title description footer show-close-button? on-close class-name
                      header-class-name body-class-name footer-class-name]
               :or {show-close-button? true
                    on-close #(re/dispatch [:dialog/hide])}}
              & contents]
  ;; The dialog is intentionally non-modal: Radix's modal focus trap steals focus from
  ;; anything portaled outside the dialog content, which breaks the search input of
  ;; popover-based dropdowns opened from inside the dialog. Non-modal Radix dialogs
  ;; render no overlay, so it is recreated below, and interactions inside popper
  ;; portals (open dropdowns) must not count as "outside" dismissals.
  [up-dialog {:open true
              :modal false
              :on-open-change (fn [open?]
                                (when-not open?
                                  (on-close)))}
   [up-dialog-portal
    [:div {:class-name "fixed inset-0 z-50 bg-up-background-underlay_60 backdrop-blur-lg"}]]
   [up-dialog-content {:show-close-button show-close-button?
                       :class-name class-name
                       ;; Dismiss only on real pointer interactions — portaled dropdown
                       ;; content taking focus must not count as leaving the dialog.
                       :on-focus-outside (fn [e]
                                           (.preventDefault e))
                       ;; While a popper (open dropdown/popover) is up, an "outside"
                       ;; interaction is dismissing IT, not the dialog. Checking for an
                       ;; open popper rather than the event target matters: a MODAL
                       ;; popover disables pointer events elsewhere, so its closing click
                       ;; reports an outside target even when visually inside the dialog.
                       :on-interact-outside (fn [e]
                                              (when (js/document.querySelector "[data-radix-popper-content-wrapper]")
                                                (.preventDefault e)))}
    (when (or title description)
      [up-dialog-header {:class-name header-class-name}
       (when title
         [up-dialog-title title])
       (when description
         [up-dialog-description description])])
    (into [up-dialog-body {:class-name body-class-name}]
          contents)
    (when footer
      [up-dialog-footer {:class-name footer-class-name}
       footer])]])

(defmethod dialog-view
  :dialog/confirm-dialog [_ {:keys [title message confirm-btn-args deny-btn-args on-confirm on-deny destructive?]
                             :or {on-deny #(re/dispatch [:dialog/hide])}}]
  [dialog {:title (or title "Confirm")
           :on-close on-deny
           :footer [:<>
                    [button/up-button (merge {:variant (if destructive? "destructive" "callToAction")
                                              :on-click on-confirm}
                                             confirm-btn-args)
                     "Yes"]
                    [button/up-button (merge {:on-click on-deny}
                                             deny-btn-args)
                     "No"]]}
   (or message "Are you sure you want to perform this action?")])

(defn active-dialog []
  (let [active-dialog- (re/subscribe [:dialog/active-])]
    (when @active-dialog-
      (let [[dialog-id args] @active-dialog-]
        ^{:key (pr-str args)}
        [dialog-view dialog-id args]))))
